import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    ContentState,
    Editor,
    EditorState,
    RichUtils,
    convertToRaw,
    Modifier,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from 'html-to-draftjs'
import Toolbar from './toolbar';

interface Props {
    defaultValue?: string
    placeholder?: string
    onChange: (value: string) => void
    disabled?: boolean
    onBlur?: (value: string) => void
    onFocus?: () => void
    minHeight?: number
    maxHeight?: number
    initialHeight?: number
    resizable?: boolean
}

export interface TextEditorRef {
    insertText: (text: string) => void;
    focus: () => void;
}

const TextEditor = forwardRef<TextEditorRef, Props>((props, ref) => {
    let defVal = EditorState.createEmpty();
    if (props.defaultValue) {
        const contentBlock = htmlToDraft(props.defaultValue);
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        defVal = EditorState.createWithContent(contentState)
    }

    const {
        minHeight = 208, // Default 52 * 4 (min-h-52)
        maxHeight = 600,
        initialHeight = 208,
        resizable = true
    } = props;

    const editor = useRef(null);
    const editorContainer = useRef<HTMLDivElement>(null);
    const [editorState, setEditorState] = useState<EditorState>(defVal);
    const [currentHeight, setCurrentHeight] = useState<number>(initialHeight);
    const [isResizing, setIsResizing] = useState(false);
    const previousDefaultValue = useRef<string | undefined>(props.defaultValue);
    const lastUserContent = useRef<string>('');

    // Handle mouse events for resizing
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!resizable) return;
        e.preventDefault();
        setIsResizing(true);

        const startY = e.clientY;
        const startHeight = currentHeight;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaY = e.clientY - startY;
            const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY));
            setCurrentHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Update editor state when defaultValue prop changes
    useEffect(() => {
        // Check if defaultValue has changed and it's different from what user last typed
        const hasDefaultValueChanged = previousDefaultValue.current !== props.defaultValue;
        const isExternalChange = props.defaultValue !== lastUserContent.current;

        if (hasDefaultValueChanged && isExternalChange) {
            console.log('Updating editor state from defaultValue prop:', props.defaultValue);
            previousDefaultValue.current = props.defaultValue;

            if (props.defaultValue) {
                const contentBlock = htmlToDraft(props.defaultValue);
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                const newEditorState = EditorState.createWithContent(contentState);
                setEditorState(newEditorState);
                lastUserContent.current = props.defaultValue;
            } else {
                // If defaultValue is empty/undefined, create empty editor state
                setEditorState(EditorState.createEmpty());
                lastUserContent.current = '';
            }
        } else if (hasDefaultValueChanged) {
            // Update the ref even if we don't update the editor state
            previousDefaultValue.current = props.defaultValue;
        }
    }, [props.defaultValue]);

    const handleKeyCommand = (command: Draft.DraftEditorCommand, editorState: EditorState): Draft.DraftHandleValue => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            setEditorState(newState);
            return 'handled';
        }
        return 'not-handled';
    };

    const styleMap = {
        CODE: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
            fontSize: 16,
            padding: 0,
        },
    };

    const handleReturn = (e: React.KeyboardEvent, editorState: EditorState) => {
        if (e.shiftKey) {
            // Sisipkan soft newline (seperti <br>)
            setEditorState(RichUtils.insertSoftNewline(editorState));
            return 'handled';
        }
        return 'not-handled';
    };

    // Expose methods through ref
    useImperativeHandle(ref, () => ({
        insertText: (text: string) => {
            const currentContent = editorState.getCurrentContent();
            const currentSelection = editorState.getSelection();

            const newContent = Modifier.insertText(
                currentContent,
                currentSelection,
                text
            );

            const newEditorState = EditorState.push(
                editorState,
                newContent,
                'insert-characters'
            );

            setEditorState(newEditorState);
            const htmlContent = draftToHtml(convertToRaw(newContent));
            lastUserContent.current = htmlContent;
            props.onChange(htmlContent);
        },
        focus: () => {
            if (editor.current) {
                // Focus the Draft.js editor
                (editor.current as { focus: () => void }).focus();
            }
        }
    }), [editorState, props]);

    return (
        <div className="border border-gray-300 rounded-md bg-white relative">
            <Toolbar editorState={editorState} setEditorState={setEditorState} />
            <div
                ref={editorContainer}
                className={`rich-editor-container p-3 overflow-y-auto relative ${props.disabled ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                style={{
                    height: `${currentHeight}px`,
                    minHeight: `${minHeight}px`,
                    maxHeight: `${maxHeight}px`
                }}
                aria-disabled={props.disabled}
            >
                <style dangerouslySetInnerHTML={{
                    __html: `
                        .rich-editor-container .public-DraftEditorPlaceholder-root {
                            color: #9ca3af !important;
                            font-size: 14px !important;
                            line-height: 1.5 !important;
                            position: absolute !important;
                            top: 12px !important;
                            left: 11px !important;
                            right: 0 !important;
                            pointer-events: none !important;
                        }
                        .rich-editor-container .public-DraftEditorPlaceholder-inner {
                            color: inherit !important;
                        }
                        .rich-editor-container .DraftEditor-root {
                            font-size: 14px;
                            line-height: 1.5;
                        }
                    `
                }} />
                <Editor
                    ref={editor}
                    readOnly={!!props.disabled}
                    tabIndex={props.disabled ? -1 : undefined}
                    handleKeyCommand={handleKeyCommand}
                    editorState={editorState}
                    customStyleMap={styleMap}
                    onChange={(newEditorState: EditorState) => {
                        if (props.disabled) return;
                        const contentState = newEditorState.getCurrentContent();
                        const htmlContent = draftToHtml(convertToRaw(contentState));
                        lastUserContent.current = htmlContent;
                        props.onChange(htmlContent);
                        setEditorState(newEditorState);
                    }}
                    onBlur={() => {
                        const contentState = editorState.getCurrentContent();
                        props.onBlur?.(draftToHtml(convertToRaw(contentState)) || "")
                    }}
                    onFocus={() => {
                        props.onFocus?.()
                    }}
                    placeholder={props.placeholder}
                    handleReturn={handleReturn}
                />
            </div>
            {resizable && (
                <div
                    className={`absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-gray-200 transition-colors ${isResizing ? 'bg-gray-300' : ''
                        }`}
                    onMouseDown={handleMouseDown}
                    title="Drag to resize"
                >
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-gray-400 rounded-full"></div>
                </div>
            )}
        </div>
    );
});

TextEditor.displayName = 'TextEditor';

export default TextEditor;